import type { ElementRef, OnInit } from '@angular/core';
import { afterNextRender, Component, inject, input, signal, viewChild } from '@angular/core';
import { marker as i18n } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { BreadcrumbList, WithContext } from 'schema-dts';

import { JsonLdComponent } from 'components/atoms/json-ld/json-ld.component';
import { AbstractWebsiteComponent } from 'components/molecules/abstract-website-component/abstract-website-component';
import { ThemeDirective } from 'src/directives/theme/theme.directive';
import { JsonLdService } from 'src/services/json-ld/json-ld.service';
import type { TFullColorScheme } from 'src/shared/types/color-schemes-and-themes';

import type { IBreadcrumbItem } from './entities/types';

@Component({
    selector: 'app-breadcrumbs',
    templateUrl: './breadcrumbs.component.html',
    styleUrls: ['./breadcrumbs.component.scss'],
    imports: [TranslatePipe, JsonLdComponent, ThemeDirective],
})
export class BreadcrumbsComponent extends AbstractWebsiteComponent implements OnInit {
    public readonly COMPONENT_NAME = 'breadcrumbs';
    private readonly jsonLdService = inject(JsonLdService);
    private readonly translateService = inject(TranslateService);

    public breadcrumbs = input<IBreadcrumbItem[]>([]);
    public colorScheme = input<TFullColorScheme>('white');

    public readonly breadcrumbsList = viewChild<ElementRef<HTMLOListElement>>('breadcrumbsList');

    public jsonLd: WithContext<BreadcrumbList> | void;
    public showLeftGradient = signal(false);
    public showRightGradient = signal(false);

    constructor() {
        super();
        afterNextRender(() => {
            const element = this.breadcrumbsList()?.nativeElement;
            if (element) {
                element.scrollLeft = 0;
            }
            this.updateGradients();
        });
    }

    ngOnInit() {
        this.jsonLd = this.getJsonLd();
    }

    public onScroll() {
        this.updateGradients();
    }

    private updateGradients() {
        const element = this.breadcrumbsList()?.nativeElement;
        if (!element) {
            return;
        }

        const { scrollLeft, scrollWidth, clientWidth } = element;
        const maxScrollLeft = scrollWidth - clientWidth;
        const hasOverflow = scrollWidth > clientWidth;

        this.showLeftGradient.set(hasOverflow && scrollLeft > 0);
        this.showRightGradient.set(hasOverflow && scrollLeft < maxScrollLeft - 1);
    }

    public getJsonLd() {
        const localizedBreadcrumbs = this.breadcrumbs().map((breadcrumb, index) => {
            if (index === 0 && breadcrumb.text?.toLowerCase() === 'home') {
                return {
                    ...breadcrumb,
                    text: this.translateService.instant(i18n('Home')),
                };
            }
            return breadcrumb;
        });
        return this.jsonLdService.generateBreadcrumbs(localizedBreadcrumbs);
    }
}
